import {
  Archive,
  DeleteOutline,
  Edit,
  MoreVert,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  CardContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { TABLE_ACTIONS } from "helpers/constants";
import { FC, MouseEvent, useState } from "react";
import { ActionItem } from "./types";
import { CardOutline } from "components/Card";

const defaultActionItems: ActionItem[] = [
  {
    name: TABLE_ACTIONS.VIEW,
    icon: <Visibility />,
    color: "success",
  },
  {
    name: TABLE_ACTIONS.EDIT,
    icon: <Edit />,
  },
  {
    name: TABLE_ACTIONS.DELETE,
    icon: <DeleteOutline color="error" />,
    color: "error",
  },
  {
    name: TABLE_ACTIONS.ARCHIVE,
    icon: <Archive />,
    color: "error",
  },
];

type CardListProps = {
  data: any[];
  idColumn: string;
  columns: { id: string; label?: string; value: string | Function }[];
  handleActionClicked: (action: TABLE_ACTIONS, id: string) => void;
  allowedMenuItems: string[] | ((data: any) => string[]);
  extraActionItems: ActionItem[];
  renderCardContent?: (data: any) => React.ReactNode | undefined;
  enableMenuInSmallDevices?: boolean;
};

type CardItemProps = {
  data: any;
  idColumn: string;
  columns: { id: string; label?: string; value: string | Function }[];
  allowedMenuItems: string[] | ((data: any) => string[]);
  extraActionItems: ActionItem[];
  handleActionClicked: (action: TABLE_ACTIONS, id: string) => void;
  renderCardContent?: (data: any) => React.ReactNode | undefined;
  enableMenuInSmallDevices?: boolean;
};

const CardItem: FC<CardItemProps> = ({
  data,
  idColumn,
  columns,
  allowedMenuItems,
  extraActionItems,
  enableMenuInSmallDevices,
  handleActionClicked,
  renderCardContent,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isMenuAllowed = (menuitem: string) => {
    return typeof allowedMenuItems === "function"
      ? allowedMenuItems(data).includes(menuitem)
      : allowedMenuItems.includes(menuitem);
  };

  const showMenu =
    (typeof allowedMenuItems === "function" || allowedMenuItems.length > 0) &&
    enableMenuInSmallDevices;

  const isCornerAction = (column: { id: string; label?: string }) =>
    !column.label && column.id !== "actions" && column.id !== "domains";

  const cornerColumns = columns.filter(isCornerAction);
  const bodyColumns = renderCardContent
    ? []
    : columns.filter(
        (column) =>
          column.id !== "actions" &&
          column.id !== "domains" &&
          !isCornerAction(column),
      );
  const domainColumns = renderCardContent
    ? []
    : columns.filter((column) => column.id === "domains");

  const renderColumnValue = (column: {
    id: string;
    value: string | Function;
  }) =>
    typeof column.value === "function"
      ? column.value(data)
      : data[column.value];

  return (
    <CardOutline>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!renderCardContent &&
              bodyColumns.map((column) => (
                <Box key={column.id} my={1}>
                  <strong>{column.label ? `${column.label}:` : ""}</strong>{" "}
                  {renderColumnValue(column)}
                </Box>
              ))}
            {!renderCardContent &&
              domainColumns.map((column) => (
                <Box key={column.id} mt={1.5}>
                  {renderColumnValue(column)}
                </Box>
              ))}
            {renderCardContent ? renderCardContent(data) : null}
          </Box>
          {(showMenu || cornerColumns.length > 0) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ flexShrink: 0, flexWrap: "nowrap", mt: -0.5, mr: -1 }}
            >
              {cornerColumns.map((column) => (
                <Box
                  key={column.id}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {renderColumnValue(column)}
                </Box>
              ))}
              {showMenu && (
                <Box>
                  <Tooltip title={"Actions"}>
                    <IconButton
                      id="basic-button"
                      aria-controls={open ? "basic-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                      onClick={handleClick}
                    >
                      <MoreVert color={"primary"} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                  >
                    {[...extraActionItems, ...defaultActionItems].map(
                      (item) => {
                        return (
                          isMenuAllowed(item.name) && (
                            <MenuItem
                              key={item.name}
                              onClick={() => {
                                handleActionClicked(item.name, data[idColumn]);
                                handleClose();
                              }}
                              color={item.color ?? "primary"}
                            >
                              <ListItemIcon>{item.icon}</ListItemIcon>
                              <ListItemText
                                sx={{
                                  color: item.color ? item.color : "default",
                                }}
                              >
                                {item.name}
                              </ListItemText>
                            </MenuItem>
                          )
                        );
                      },
                    )}
                  </Menu>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </CardContent>
    </CardOutline>
  );
};

export const CardList: FC<CardListProps> = ({
  data,
  idColumn,
  allowedMenuItems,
  extraActionItems,
  columns,
  handleActionClicked,
  renderCardContent,
  enableMenuInSmallDevices,
}) => {
  return (
    <Box>
      <Stack spacing={2} direction="column">
        {data.map((item) => (
          <CardItem
            key={item[idColumn]}
            allowedMenuItems={allowedMenuItems}
            extraActionItems={extraActionItems}
            handleActionClicked={handleActionClicked}
            data={item}
            idColumn={idColumn}
            columns={columns}
            renderCardContent={renderCardContent}
            enableMenuInSmallDevices={enableMenuInSmallDevices}
          />
        ))}
      </Stack>
    </Box>
  );
};
